import { Component, OnInit, OnDestroy, ApplicationRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { AppService } from '../services/app.service';
import { SessionStorageService } from '../services/session-storage.service';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AppointmentDetails } from '../services/client.service';
import { MatDialogComponent } from '../mat-dialog/mat-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-service-appointment-details',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatButtonModule, MatIconModule,MatProgressSpinnerModule],
  templateUrl: './service-appointment-details.component.html',
  styleUrls: ['./service-appointment-details.component.scss']
})
export class ServiceAppointmentDetailsComponent implements OnInit, OnDestroy {
  appointmentDetails: any = null;
  loading = true;
  error: string | null = null;
  locationDetails: any = null;
  private unsubscribe$ = new Subject<void>();
  isCancelpage: boolean = false;
  cancelAppointmentText: string = $localize `Cancel appointment`;
  newAppointmentText: string = $localize `New appointment`;
  missingAppointmentIdErrorText:string = $localize`Appointment ID is missing`;
  pageReady = false;
  private appStable = false;
  vehicleInfo: any;
  isappointmentPage: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private appService: AppService,
    private sessionStorageService: SessionStorageService,
    private dialog: MatDialog,
    private appRef: ApplicationRef
  ) {}

  ngOnInit(): void {
    this.isCancelpage = this.router.url.includes('cancel');
    this.loadData();
    
    this.appRef.isStable
      .pipe(
        filter(stable => stable),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() => {
        this.appStable = true;
        if (!this.loading) {
          this.updatePageReady();
        }
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() => {
        const cancelPage = this.isCancelpage;
        this.isCancelpage = this.router.url.includes('cancel');
        if (this.isCancelpage && !cancelPage) {
          this.appointmentDetails = null;
        }
        
        this.loadData();
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private loadData(): void {
    this.error = null;
    this.loading = true;
    this.pageReady = false;
    this.updatePageReady();
    this.checkForAppointmentData();
  }

  private checkForAppointmentData(attempts: number = 0): void {
    const maxAttempts = 10;
    
    const appointmentDataString = this.sessionStorageService.getSessionItem('appointmentData');
    if (appointmentDataString) {
      try {
        this.appointmentDetails = JSON.parse(appointmentDataString);
        this.loadLocationDetails();
        this.loading = false;
        this.error = null; 
        this.updatePageReady();
        return;
      } catch (e) {
        console.error('Error parsing appointment data:', e);
      }
    }

    const appointmentId = this.sessionStorageService.getSessionItem('appointmentId');
    if (appointmentId) {
      this.loadAppointmentDetails(appointmentId);
      return;
    }

    if (attempts < maxAttempts) {
      setTimeout(() => {
        this.checkForAppointmentData(attempts + 1);
      }, 100);
      return;
    }
    
    setTimeout(() => {
      const finalDataCheck = this.sessionStorageService.getSessionItem('appointmentData');
      const finalIdCheck = this.sessionStorageService.getSessionItem('appointmentId');
      
      if (finalDataCheck) {
        try {
          this.appointmentDetails = JSON.parse(finalDataCheck);
          this.loadLocationDetails();
          this.loading = false;
          this.error = null;
          this.updatePageReady();
          return;
        } catch (e) {
          console.error('Error parsing appointment data on final check:', e);
        }
      }
      
      if (finalIdCheck) {
        this.loadAppointmentDetails(finalIdCheck);
        return;
      }
    }, 200);
  }

  private loadLocationDetails(): void {
    if (!this.appointmentDetails?.locationId) return;

    const companiesListString = this.sessionStorageService.getSessionItem('companiesList');
    if (companiesListString) {
      const companiesList = JSON.parse(companiesListString);
      
      for (const company of companiesList) {
        const locations = company.locations || company.Locations;
        if (locations) {
          const location = locations.find((loc: any) =>
            (loc.id || loc.Id) === this.appointmentDetails.locationId
          );
          if (location) {
            this.locationDetails = {
              name: location.description || location.Description,
              address: location.address || location.Address,
              company: {
                name: company.name || company.Name,
                description: company.description || company.Description
              }
            };
            break;
          }
        }
      }
    }
  }

  loadAppointmentDetails(appointmentId: string): void {
    this.loading = true;
    this.error = null;
    
    this.appService.getAppointment(appointmentId).subscribe({
      next: (details: AppointmentDetails) => {
        this.appointmentDetails = details;
        this.sessionStorageService.setSessionItem('appointmentData', JSON.stringify(details));
        this.loadLocationDetails();
        this.loading = false;
        this.error = null; 
        this.updatePageReady();
      },
      error: (error) => {
        this.appService.handleErrorMessage(error);
        this.error = $localize`No appointment data available`;
        this.loading = false;
        this.updatePageReady();
      }
    });
  }

  private getCurrentLocale(): string {
    try {
      const stored = window.localStorage.getItem('lang');
      return stored || (navigator?.language ?? 'en-US');
    } catch {
      return 'en-US';
    }
  }

  private getHour12Preference(): boolean | null {
    const locale = this.getCurrentLocale();
    try {
      const resolved = new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions();
      if (resolved.hourCycle === 'h11' || resolved.hourCycle === 'h12') return true;
      if (resolved.hourCycle === 'h23' || resolved.hourCycle === 'h24') return false;
    } catch {}
    try {
      const probe = new Date();
      const out = probe.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
      return /13/.test(out) ? false : true;
    } catch {}
    return null;
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const locale = this.getCurrentLocale();
    try {
      return date.toLocaleDateString(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      });
    } catch {
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      });
    }
  }

  formatTime(dateString: string): string {
    const date = new Date(dateString);
    const locale = this.getCurrentLocale();
    const hour12Pref = this.getHour12Preference();
    try {
      return date.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        ...(hour12Pref === null ? {} : { hour12: hour12Pref })
      } as Intl.DateTimeFormatOptions);
    } catch {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }

  getTotalPrice(): number {
    if (!this.appointmentDetails?.services) return 0;
    return this.appointmentDetails.services.reduce((total: number, service: any) => {
      const price = this.getServicePrice(service);
      return total + (price || 0);
    }, 0);
  }

  getServicePrice(service: any): number {
    if (!service?.unitPrice) return 0;

    if (service.unitPrice.grossPrice) {
      return service.unitPrice.grossPrice;
    } else if (service.unitPrice.netPrice) {
      return service.unitPrice.netPrice;
    }
    return 0;
  }

  getServicePriceDisplay(service: any): string {
    if (!service?.unitPrice) return '';

    const currency = service.unitPrice.currency;

    if (service.unitPrice.grossPrice) {
      return `${service.unitPrice.grossPrice.toFixed(0)} ${currency}`;
    } else if (service.unitPrice.netPrice) {
      return `${service.unitPrice.netPrice.toFixed(0)} ${currency}`;
    }
    return '';
  }

 getServicePriceNote(service: any): string {
  if (!service?.unitPrice) return '';

  if (service.unitPrice.grossPrice) {
    return $localize`(incl. VAT)`;
  } else if (service.unitPrice.netPrice) {
    return $localize`(excl. VAT)`;
  }
  return '';
}

  hasServicePrice(service: any): boolean {
  return !!(service?.unitPrice?.grossPrice || service?.unitPrice?.netPrice);
}

  goBack(): void {
    window.history.back();
  }

  getVehicleVin(): string {
    return this.appointmentDetails.vehicle.vin || $localize`VIN not provided by User`;
  }
  cancelAppointment(): void {
    const dialogRef = this.dialog.open(MatDialogComponent, {
      data: {
        dialogType: 'cancelAppointment'
      },
      panelClass: 'cancel-appointment-dialog'
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        const buttonName = result.buttonName ?? result;
        const appointmentId = this.appointmentDetails?.id;
        this.loading = true;
        this.isappointmentPage = true;
        this.cancelAppointmentCall(buttonName, appointmentId);
      }
    });
  }

  cancelAppointmentCall(buttonName: boolean, appointmentId: string | undefined): void {
    if (!buttonName) {
      return;
    }
    if (!appointmentId) {
      this.error = this.missingAppointmentIdErrorText;
      this.loading = false;
      return;
    }
    this.loading = true;
    this.error = null;
    this.updatePageReady();

    this.appService.cancelAppointment(appointmentId).subscribe({
      next: () => {
        this.sessionStorageService.setSessionItem('appointmentData', '');
        this.loading = false;
        this.updatePageReady();
        this.router.navigate(['/cancel']);
      },
      error: (err: any) => {
        this.appService.handleErrorMessage(err);
        const errorDetail = err?.detail?.toLowerCase?.();
        if (errorDetail?.includes('already been cancelled') || errorDetail?.includes('already been canceled')) {
          this.router.navigate(['/cancel']);
        }
        this.loading = false;
        this.updatePageReady();
      }
    });
  }

  goToCancel() {
    this.router.navigate(['/cancel']);
  }

  close() {
    if (this.isCancelpage) {
      this.sessionStorageService.removeSessionItem('appointmentData');
    }
    this.router.navigate(['/locations']);
  }

  private updatePageReady(): void {
    this.pageReady = !this.loading && !!this.appointmentDetails && !this.error;
  }
}